import { Checkbox, FormControlLabel } from '@mui/material'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { TimeSeriesDataTableColumnDef } from '../TimeSeriesDataTable/types'
import getRowIndex from '../TimeSeriesDataTable/utils/getRowIndex'

import styles from './TimeSeriesSettingTable.module.less'

export function useCustomCheckboxColumn({
  datasets,
  enableComputableSettings,
  hasDataColumns,
  selectedRows,
  setSelectedRows,
  toggleRowSelection,
  shiftKeyPressed,
}: {
  datasets: Dataset[]
  enableComputableSettings?: boolean
  hasDataColumns: boolean
  selectedRows: number[]
  setSelectedRows: (rows: number[]) => void
  toggleRowSelection: (rowIndex: number, checked: boolean, shiftKeyPressed: boolean) => void
  shiftKeyPressed: boolean
}): [TimeSeriesDataTableColumnDef[], number[], () => void] {
  const { t } = useTranslation()

  const columns: TimeSeriesDataTableColumnDef[] = []
  if (enableComputableSettings && hasDataColumns) {
    columns.push({
      accessorKey: `computed`,
      enableColumnActions: false,
      enableEditing: true,
      muiTableHeadCellProps: {
        align: 'center',
      },
      align: 'center',
      maxSize: 85,
      minSize: 85,
      header: '',
      Header: ({ table }) => {
        const { pageSize, pageIndex } = table.getState().pagination
        const currentPageStartIndex = pageIndex * pageSize
        const currentPageEndIndex = Math.min((pageIndex + 1) * pageSize, datasets[0].values.length) - 1
        const range = Array.from(
          { length: currentPageEndIndex - currentPageStartIndex + 1 },
          (_, i) => i + currentPageStartIndex
        )
        const allOfCurrentPageAreSelected = range.every((index) => selectedRows.includes(index))
        return (
          <>
            <div className={styles.DatasetTableHeader}>
              <span>{t(`Computed setting`)}</span>
            </div>
            <div>
              <FormControlLabel
                label={t('Select all')}
                labelPlacement="top"
                control={
                  <Checkbox
                    checked={allOfCurrentPageAreSelected}
                    onChange={(_event, checked) => {
                      setSelectedRows(checked ? range : [])
                    }}
                  />
                }
                slotProps={{ typography: { fontSize: '12px', fontWeight: 200 } }}
              />
            </div>
          </>
        )
      },
      Cell: ({ renderedCellValue }) => <span style={{ whiteSpace: `nowrap` }}>{renderedCellValue}</span>,
      Edit: ({ row, table }) => {
        const datasetIndex = getRowIndex(row, table.getState().pagination)
        return (
          <Checkbox
            checked={selectedRows.includes(datasetIndex)}
            onChange={(_event, checked) => {
              toggleRowSelection(datasetIndex, checked, shiftKeyPressed)
            }}
          />
        )
      },
    })
  }

  const reset = useCallback(() => {
    setSelectedRows([])
  }, [setSelectedRows])
  return [columns, selectedRows, reset]
}
